import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { supabase } from "../../lib/supabase";
import { useExpenseDocumentImports } from "../../hooks/useData";
import { formatDate } from "../../lib/finance";
import { useSchoolYear } from "../../context/SchoolYearContext";
import { applyExpenseOcrPrefill, EXPENSE_OCR_PREFILL_FIELDS } from "../../lib/expenseDocumentOcrPrefill";

function documentTypeLabel(value) {
  return value === "receipt" ? "Paragon" : value === "invoice" ? "Faktura" : "Nieznany";
}

function ocrMoney(value, currency) {
  if (value === null || value === undefined) return "—";
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";
  return `${amount.toFixed(2)} ${currency ?? ""}`.trim();
}

function OcrValue({ label, value }) {
  return <div><span className="text-slate-400">{label}:</span> {value ?? "—"}</div>;
}

function safeGoogleDriveUrl(value) {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && url.hostname === "drive.google.com"
      ? url.toString()
      : null;
  } catch {
    return null;
  }
}

function DocumentReviewCard({ item, schoolYears, onChanged }) {
  const [submitting, setSubmitting] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrError, setOcrError] = useState("");
  const [ocrData, setOcrData] = useState(item.ocr_data ?? null);
  const dirtyFields = useRef({});
  const [form, setForm] = useState(() => applyExpenseOcrPrefill({
    occasion: item.reimbursement_person
      ? `Zwrot - ${item.reimbursement_person}`
      : "Zwrot za zakup klasowy",
    purchaseDate: "",
    itemOrService: "Zakup klasowy",
    totalValue: "",
    confirmationType:
      item.document_mime_type === "application/pdf" ? "Faktura / PDF" : "Paragon",
    reimbursementPerson: item.reimbursement_person ?? "",
    schoolYearId: item.school_year_id,
  }, item.ocr_data));

  useEffect(() => {
    setOcrData(item.ocr_data ?? null);
    setForm((current) => applyExpenseOcrPrefill(current, item.ocr_data, dirtyFields.current));
  }, [item.ocr_data]);

  function set(name, value) {
    if (EXPENSE_OCR_PREFILL_FIELDS.includes(name)) dirtyFields.current[name] = true;
    setForm((current) => ({ ...current, [name]: value }));
  }

  async function previewDocument() {
    const url = safeGoogleDriveUrl(item.google_drive_url);
    if (!url) {
      toast.error("Dokument nie ma poprawnego prywatnego linku Google Drive.");
      return;
    }

    window.open(url, "_blank", "noopener,noreferrer");
  }

  async function runOcr() {
    if (ocrData && !window.confirm("Uruchomić OCR ponownie? Poprzedni wynik zostanie zastąpiony po udanym odczycie.")) {
      return;
    }

    setOcrLoading(true);
    setOcrError("");
    let result;
    try {
      result = await supabase.functions.invoke("extract-expense-document-ocr", {
        body: { expense_document_import_id: item.id },
      });
    } catch {
      result = { error: true };
    } finally {
      setOcrLoading(false);
    }

    if (result.error || result.data?.status !== "completed" || !result.data?.ocr_data) {
      setOcrError("Nie udało się odczytać dokumentu. Poprzedni wynik, jeśli istniał, nie został zmieniony.");
      return;
    }

    setOcrData(result.data.ocr_data);
    setForm((current) => applyExpenseOcrPrefill(current, result.data.ocr_data, dirtyFields.current));
    onChanged();
    toast.success("OCR dokumentu został zakończony.");
  }

  async function updateSchoolYear(schoolYearId) {
    setSubmitting(true);
    const { error } = await supabase
      .from("expense_document_imports")
      .update({ school_year_id: schoolYearId })
      .eq("id", item.id)
      .in("approval_status", ["pending", "manual_review"])
      .select("id")
      .single();
    setSubmitting(false);

    if (error) {
      toast.error("Nie udało się zmienić roku szkolnego.");
      return;
    }

    set("schoolYearId", schoolYearId);
    toast.success("Rok szkolny został zaktualizowany.");
  }

  async function approve() {
    if (!form.occasion.trim() || !form.purchaseDate || !form.itemOrService.trim()) {
      toast.error("Uzupełnij okazję, datę zakupu i opis.");
      return;
    }

    const totalValue = Number(String(form.totalValue).replace(",", "."));
    if (!Number.isFinite(totalValue) || totalValue <= 0) {
      toast.error("Podaj poprawną kwotę większą od zera.");
      return;
    }

    setSubmitting(true);
    const { error: reviewUpdateError } = await supabase
      .from("expense_document_imports")
      .update({ reimbursement_person: form.reimbursementPerson.trim() || null })
      .eq("id", item.id)
      .in("approval_status", ["pending", "manual_review"])
      .select("id")
      .single();

    if (reviewUpdateError) {
      setSubmitting(false);
      toast.error("Nie udało się zapisać danych do weryfikacji.");
      return;
    }

    const { error } = await supabase.rpc("approve_expense_document", {
      p_import_id: item.id,
      p_occasion: form.occasion.trim(),
      p_purchase_date: form.purchaseDate,
      p_item_or_service: form.itemOrService.trim(),
      p_total_value: totalValue,
      p_confirmation_type: form.confirmationType.trim() || null,
      p_notes: form.reimbursementPerson.trim()
        ? `Zwrot dla: ${form.reimbursementPerson.trim()}`
        : null,
    });
    setSubmitting(false);

    if (error) {
      toast.error("Nie udało się zatwierdzić dokumentu.");
      return;
    }

    toast.success("Wydatek został zatwierdzony.");
    onChanged();
  }

  async function reject() {
    if (!window.confirm("Odrzucić ten dokument bez tworzenia wydatku?")) return;

    setSubmitting(true);
    const { error } = await supabase.rpc("reject_expense_document", {
      p_import_id: item.id,
    });
    setSubmitting(false);

    if (error) {
      toast.error("Nie udało się odrzucić dokumentu.");
      return;
    }

    toast.success("Dokument został odrzucony.");
    onChanged();
  }

  return (
    <article className="rounded-[28px] border border-white/70 bg-white/85 p-5 shadow-[0_18px_48px_rgba(15,23,42,0.06)]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.14em] text-amber-700">
            {item.approval_status === "manual_review" ? "Wymaga ręcznej weryfikacji" : "Oczekuje"}
          </div>
          <h2 className="mt-2 text-lg font-semibold text-slate-900">
            {item.reimbursement_person ? `Zwrot — ${item.reimbursement_person}` : "Dokument wydatku"}
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Wpłynął: {formatDate(item.created_at)} · {item.document_mime_type}
          </p>
        </div>
        <button type="button" onClick={previewDocument} className="btn-ghost px-4 py-2 text-sm">
          Podgląd
        </button>
      </div>

      <section className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4" aria-live="polite">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold text-slate-900">Odczyt dokumentu</h3>
            <p className="mt-1 text-xs text-slate-500">Dane OCR są niezweryfikowaną podpowiedzią. Wstępnie uzupełniony formularz wymaga kontroli administratora.</p>
          </div>
          <button type="button" className="btn-ghost px-4 py-2 text-sm" disabled={ocrLoading || submitting} onClick={runOcr}>
            {ocrLoading ? "Odczytywanie…" : ocrData ? "Uruchom OCR ponownie" : "Uruchom OCR"}
          </button>
        </div>

        {ocrLoading && <p className="mt-4 text-sm text-slate-600">Dokument jest analizowany. Nie zamykaj tej karty.</p>}
        {ocrError && <p className="mt-4 text-sm font-medium text-rose-700" role="alert">{ocrError}</p>}
        {!ocrLoading && !ocrData && !ocrError && <p className="mt-4 text-sm text-slate-500">OCR nie został jeszcze wykonany.</p>}
        {ocrData && (
          <div className="mt-4 space-y-4 text-sm">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <OcrValue label="Typ dokumentu" value={documentTypeLabel(ocrData.document_type)} />
              <OcrValue label="Sprzedawca" value={ocrData.merchant?.name} />
              <OcrValue label="NIP" value={ocrData.merchant?.nip} />
              <OcrValue label="Numer dokumentu" value={ocrData.document_number} />
              <OcrValue label="Data wystawienia" value={ocrData.issue_date} />
              <OcrValue label="Waluta" value={ocrData.currency} />
              <OcrValue label="Netto" value={ocrMoney(ocrData.totals?.net, ocrData.currency)} />
              <OcrValue label="VAT" value={ocrMoney(ocrData.totals?.vat, ocrData.currency)} />
              <OcrValue label="Brutto" value={ocrMoney(ocrData.totals?.gross, ocrData.currency)} />
            </div>
            {ocrData.confidence != null && (
              <p className={ocrData.confidence < 0.7 ? "font-medium text-amber-700" : "text-slate-600"}>
                Pewność odczytu: {Math.round(ocrData.confidence * 100)}%
                {ocrData.confidence < 0.7 ? " — wynik wymaga szczególnie uważnej kontroli." : ""}
              </p>
            )}
            {ocrData.items?.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-xs">
                  <thead className="text-slate-500"><tr><th className="py-2 pr-3">Pozycja</th><th className="py-2 pr-3">Ilość</th><th className="py-2 pr-3">Cena</th><th className="py-2">Razem</th></tr></thead>
                  <tbody>{ocrData.items.map((entry, index) => (
                    <tr key={`${entry.name ?? "pozycja"}-${index}`} className="border-t border-slate-200">
                      <td className="py-2 pr-3">{entry.name ?? "—"}</td>
                      <td className="py-2 pr-3">{entry.quantity ?? "—"}</td>
                      <td className="py-2 pr-3">{ocrMoney(entry.unit_price, ocrData.currency)}</td>
                      <td className="py-2">{ocrMoney(entry.total, ocrData.currency)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
            {ocrData.warnings?.length > 0 && (
              <div className="rounded-xl bg-amber-50 p-3 text-amber-900">
                <div className="font-semibold">Ostrzeżenia OCR</div>
                <ul className="mt-1 list-disc space-y-1 pl-5">{ocrData.warnings.map((warning, index) => <li key={`${warning}-${index}`}>{warning}</li>)}</ul>
              </div>
            )}
          </div>
        )}
      </section>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="text-sm text-slate-600 sm:col-span-2">
          Rok szkolny
          <select
            className="input mt-1"
            value={form.schoolYearId}
            disabled={submitting}
            onChange={(event) => updateSchoolYear(event.target.value)}
          >
            {schoolYears.map((year) => (
              <option key={year.id} value={year.id}>
                {year.label} · {year.status}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-slate-600 sm:col-span-2">
          Osoba do zwrotu (propozycja)
          <input
            className="input mt-1"
            value={form.reimbursementPerson}
            onChange={(event) => set("reimbursementPerson", event.target.value)}
            placeholder="np. Tomek"
          />
        </label>
        <label className="text-sm text-slate-600">
          Okazja
          <input className="input mt-1" value={form.occasion} onChange={(e) => set("occasion", e.target.value)} />
        </label>
        <label className="text-sm text-slate-600">
          Data zakupu
          <input className="input mt-1" type="date" value={form.purchaseDate} onChange={(e) => set("purchaseDate", e.target.value)} />
        </label>
        <label className="text-sm text-slate-600">
          Towar / usługa
          <input className="input mt-1" value={form.itemOrService} onChange={(e) => set("itemOrService", e.target.value)} />
        </label>
        <label className="text-sm text-slate-600">
          Kwota
          <input className="input mt-1" type="number" min="0.01" step="0.01" value={form.totalValue} onChange={(e) => set("totalValue", e.target.value)} />
        </label>
      </div>

      <div className="mt-5 flex flex-wrap justify-end gap-3">
        <button type="button" className="btn-ghost" disabled={submitting} onClick={reject}>
          Odrzuć
        </button>
        <button type="button" className="btn-primary" disabled={submitting} onClick={approve}>
          Zatwierdź i utwórz wydatek
        </button>
      </div>
    </article>
  );
}

export default function ExpenseDocumentsScreen() {
  const { data, count, loading } = useExpenseDocumentImports();
  const { schoolYears = [] } = useSchoolYear();

  const sorted = useMemo(
    () => [...data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [data]
  );

  function handleChanged() {
    window.dispatchEvent(new Event("skarbnik:expense-document-imports-updated"));
  }

  return (
    <div className="space-y-6">
      <header>
        <div className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">
          Administracja
        </div>
        <h1 className="mt-2 text-3xl font-semibold text-slate-900">Dokumenty do sprawdzenia</h1>
        <p className="mt-2 text-sm text-slate-500">
          Oczekujące i wymagające ręcznej weryfikacji: {count}
        </p>
      </header>

      {loading && <div className="card p-8 text-slate-500">Ładowanie dokumentów…</div>}
      {!loading && sorted.length === 0 && (
        <div className="card p-8 text-slate-500">Brak dokumentów oczekujących na sprawdzenie.</div>
      )}
      {!loading && sorted.map((item) => (
        <DocumentReviewCard
          key={item.id}
          item={item}
          schoolYears={schoolYears}
          onChanged={handleChanged}
        />
      ))}
    </div>
  );
}
